import { extractionRationale } from "./enrichmentRationale";

/** LLM should supply industry definitions; never fall back to rationale for these. */
export const INDUSTRY_CATALOG_ENTITY_TYPES = new Set([
  "technology-component",
  "platform",
]);

const WEAK_CATALOG_MARKERS = /enterprise architecture catalog/i;

function normalizeEntityType(entityType) {
  return String(entityType || "").trim().toLowerCase();
}

function explicitCatalogText(row) {
  const cd = row?.entity_catalog_description ?? row?.catalog_description;
  return cd && String(cd).trim() ? String(cd).trim() : "";
}

export function isWeakCatalogDescription(text, entityName = "") {
  const cd = String(text || "").trim();
  if (!cd) return true;
  if (cd.length < 25) return true;
  if (WEAK_CATALOG_MARKERS.test(cd)) return true;
  const nm = String(entityName || "").trim().toLowerCase();
  if (nm && cd.toLowerCase() === nm) return true;
  return false;
}

function resolvedCatalogText(row, { entityType, entityName } = {}) {
  const name = String(
    entityName || row?.name || row?.missing_surface_name || ""
  ).trim();
  const cd = explicitCatalogText(row);
  if (cd && !isWeakCatalogDescription(cd, name)) return cd;
  return "";
}

/**
 * Description persisted on a new catalog entity at review approve.
 */
export function entityCatalogDescription(row, { entityType, entityName } = {}) {
  const et = normalizeEntityType(
    entityType || row?.missing_entity_type || row?.entity_type || row?.type
  );
  const catalog = resolvedCatalogText(row, { entityType: et, entityName });
  if (catalog) return catalog;
  if (INDUSTRY_CATALOG_ENTITY_TYPES.has(et)) {
    return "";
  }
  return extractionRationale(row) || "";
}

/**
 * Review UI: catalog / entity description block (not relationship usage).
 */
export function catalogDescriptionForDisplay(
  row,
  { entityType, entityName, previewOnCreate } = {}
) {
  const et = normalizeEntityType(
    entityType || row?.missing_entity_type || row?.entity_type
  );
  const catalog = resolvedCatalogText(row, { entityType: et, entityName });
  if (catalog) return catalog;
  if (previewOnCreate) {
    return entityCatalogDescription(row, { entityType: et, entityName });
  }
  if (!INDUSTRY_CATALOG_ENTITY_TYPES.has(et)) {
    return extractionRationale(row) || "";
  }
  return "";
}

export function catalogDescriptionSectionLabel(row, entityType) {
  const et = normalizeEntityType(entityType || row?.missing_entity_type);
  if (resolvedCatalogText(row, { entityType: et })) {
    return INDUSTRY_CATALOG_ENTITY_TYPES.has(et)
      ? "Catalog description"
      : "Description";
  }
  if (INDUSTRY_CATALOG_ENTITY_TYPES.has(et)) {
    return "Catalog description";
  }
  return "Description";
}

/**
 * Single review-card description: prefer association/usage text (richer), else catalog.
 */
export function enrichmentContextDescriptionForDisplay(
  row,
  { entityType, entityName, anchorName, previewOnCreate } = {}
) {
  const usage = relationshipAssociationDescription(row, { anchorName });
  if (usage) return usage;
  return (
    catalogDescriptionForDisplay(row, {
      entityType,
      entityName,
      previewOnCreate,
    }) || ""
  );
}

/**
 * Association / relationship notes: how the anchor application uses this link.
 */
export function relationshipAssociationDescription(row, { anchorName } = {}) {
  const parts = [];
  const rat = extractionRationale(row);
  if (rat) parts.push(rat);
  const flow = row?.data_flow_detail;
  if (flow && String(flow).trim()) {
    parts.push(`Flow detail: ${String(flow).trim()}`);
  }
  const anchor = String(anchorName || "").trim();
  if (parts.length === 0 && anchor) {
    parts.push(`Identified in enrichment review for ${anchor}.`);
  }
  return parts.filter(Boolean).join("\n\n");
}
